import React from 'react';
import ServiceTemplate from '@/components/ServiceTemplate';

const PrivateLimited = () => {
  const data = {
    title: 'Private Limited Company Registration',
    description: 'Register your Private Limited Company with complete legal compliance and expert guidance. Get incorporated in 10-15 days with full MCA support.',
    keywords: 'private limited company registration, company registration in india, pvt ltd company, business incorporation, MCA registration, company formation',
    
    detailedDescription: (
      <div className="space-y-4">
        <p>
          A Private Limited Company is the most popular business structure in India, offering limited liability protection 
          and easy access to funding. It's a separate legal entity that provides credibility, attracts investors, and offers 
          various tax benefits. As per the Companies Act 2013, a Private Limited Company can be formed with a minimum of 
          2 directors and 2 shareholders.
        </p>
        <p>
          <strong>Why Choose Private Limited Company?</strong> This structure is ideal for startups, small to medium businesses, 
          and entrepreneurs looking to scale. It offers protection of personal assets, makes it easier to raise funds from 
          VCs and angel investors, and provides better business continuity even if directors change. The company can also 
          issue ESOP (Employee Stock Option Plan) to attract and retain talent.
        </p>
        <p>
          <strong>Legal Framework:</strong> Private Limited Companies are governed by the Ministry of Corporate Affairs (MCA) 
          under the Companies Act 2013. They must maintain proper books of accounts, file annual returns, conduct board 
          meetings, and comply with various statutory requirements. Our expert team ensures complete compliance throughout 
          the registration process and beyond.
        </p>
        <p>
          <strong>Funding Opportunities:</strong> Private Limited Companies can easily raise funds through equity financing, 
          venture capital, angel investors, and bank loans. The structure also allows for multiple rounds of funding, making 
          it suitable for businesses with high growth potential. Foreign Direct Investment (FDI) is also permitted in most sectors.
        </p>
      </div>
    ),
    
    benefits: [
      'Limited liability protection for directors and shareholders - personal assets are protected',
      'Separate legal entity with perpetual succession - continues even if members change',
      'Easy to raise funds from investors, VCs, and banks with proper equity structure',
      'Better credibility and trust among clients, suppliers, and business partners',
      'Tax benefits including deductions on business expenses, depreciation, and losses',
      'Easy transfer of ownership through shares without affecting business operations',
      'Can have up to 200 shareholders, making it suitable for growth',
      'Foreign nationals and NRIs can be directors and shareholders',
      'Professional image enhances business reputation and market presence',
      'Better access to government tenders and business opportunities',
      'Can issue ESOP to employees for talent retention',
      'Lower tax rates compared to proprietorship (25.17% for companies with turnover < ₹400 cr)',
    ],
    
    documents: [
      'PAN Card of all Directors and Shareholders (mandatory for Indian nationals)',
      'Aadhaar Card of all Directors (for identity verification)',
      'Passport (for foreign nationals) and Visa (if applicable)',
      'Passport size photographs (recent, colored - 2 copies per person)',
      'Address proof of registered office: Rent agreement (notarized) or Sale deed',
      'Utility bill of registered office (electricity/water bill - not older than 2 months)',
      'NOC (No Objection Certificate) from property owner for using premises as registered office',
      'Digital Signature Certificate (DSC) of Directors - We can help obtain this',
      'Bank statement or passbook (recent - for registered office address proof)',
      'Director and Shareholder identity and address proof documents',
      'MOA and AOA drafts (We prepare these documents for you)',
    ],
    
    process: [
      'Obtain Digital Signature Certificate (DSC) for all directors - takes 1-2 days',
      'Apply for Director Identification Number (DIN) with MCA - processed within 1 day',
      'Name reservation through RUN (Reserve Unique Name) - submit 2 name options, approved in 1-2 days',
      'Drafting of Memorandum of Association (MOA) and Articles of Association (AOA)',
      'Preparation and verification of all incorporation documents',
      'Filing of SPICe+ form (Simplified Proforma for Incorporating Company Electronically)',
      'Application for PAN and TAN simultaneously with incorporation',
      'Certificate of Incorporation issued by MCA with CIN (Corporate Identification Number)',
      'Bank account opening with Certificate of Incorporation',
      'GST registration if required (separate process)',
    ],
    
    timeline: '10-15 Working Days',
    pricing: '₹6,999 + Govt Fees',
    
    additionalInfo: (
      <div className="space-y-3">
        <p><strong>Government Fees (Approximate):</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>Authorized Capital up to ₹1 Lakh: ₹1,000 (Stamp Duty) + ₹500 (Filing Fee)</li>
          <li>Authorized Capital ₹1 Lakh to ₹5 Lakh: ₹3,000 + ₹500</li>
          <li>DSC (per director): ₹1,500 - ₹2,000</li>
        </ul>
        <p className="mt-3"><strong>Post-Incorporation Compliance:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>First Board Meeting within 30 days of incorporation</li>
          <li>Annual ROC filing (Form AOC-4 and Form MGT-7)</li>
          <li>Income Tax Return filing</li>
          <li>Maintenance of statutory registers and minute books</li>
        </ul>
        <p className="mt-3"><strong>Our Professional Fees Include:</strong> DSC application, DIN application, name search and reservation, 
        MOA/AOA drafting, incorporation filing, PAN/TAN application, Certificate of Incorporation, and post-registration guidance.</p>
      </div>
    ),
    
    faqs: [
      {
        question: 'What is the minimum number of directors required?',
        answer: 'A Private Limited Company requires a minimum of 2 directors and can have a maximum of 15 directors. At least one director must be an Indian resident. Directors can also be shareholders.',
      },
      {
        question: 'What is the minimum capital required?',
        answer: 'There is no minimum capital requirement for Private Limited Company registration in India as per the Companies Act 2013. However, you should have reasonable authorized capital based on your business needs (commonly ₹1 lakh).',
      },
      {
        question: 'Can foreign nationals be directors?',
        answer: 'Yes, foreign nationals can be directors in an Indian Private Limited Company, subject to compliance with FEMA regulations. At least one director must be an Indian resident (resident in India for 182 days in the preceding calendar year).',
      },
      {
        question: 'Do I need a physical office address?',
        answer: 'Yes, you need a registered office address with proper documentation like rent agreement (notarized) or ownership proof (sale deed). It can be a residential or commercial property. We can also provide registered office services if needed.',
      },
      {
        question: 'Can the same person be director and shareholder?',
        answer: 'Yes, the same person can be both a director and shareholder. In fact, most small companies have the same individuals as directors and shareholders. You need minimum 2 directors and 2 shareholders (can be same people).',
      },
      {
        question: 'What is the difference between Authorized Capital and Paid-up Capital?',
        answer: 'Authorized Capital is the maximum capital your company can raise (mentioned in MOA). Paid-up Capital is the actual amount received from shareholders. You pay government fees based on Authorized Capital, but there\'s no obligation to bring in the full authorized capital.',
      },
      {
        question: 'How long does the entire process take?',
        answer: 'The complete registration process typically takes 10-15 working days, provided all documents are submitted correctly and there are no objections from MCA. DSC and DIN take 1-2 days, name approval takes 1-2 days, and incorporation takes 5-7 days.',
      },
      {
        question: 'What are the annual compliance requirements?',
        answer: 'Annual compliance includes: filing AOC-4 (financial statements), MGT-7 (annual return), Income Tax Return, holding board meetings (minimum 4 per year), maintaining statutory registers, and audit by a Chartered Accountant if turnover exceeds ₹1 crore.',
      },
    ],
  };

  return <ServiceTemplate {...data} />;
};

export default PrivateLimited;